import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useResizing } from '../tracks/dragging/useResizing';
import { useZoomDragging, useZoomHandleResize } from './zooming';
import useEditor from '../../../../hooks/useEditor';
import useForceUpdate from '../../../../hooks/useForceUpdate';
import useEditorTimeframe from '../../../../hooks/useEditorTimeframe';
import { ZoomContainer, ZoomControlBox, ZoomHandle } from './ZoomControl.styled';

interface Props {
    elementContainerRef: any;
    shotsContainerRef: any;
    timestampsContainerRef: any;
    onResizeEnd: (leftPercentage: number, widthPercentage: number) => void;
}

export const ZoomControl = ({ elementContainerRef, shotsContainerRef, timestampsContainerRef, onResizeEnd }: Props) => {
    const { creativeModel } = useEditor();
    const { duration, timelineDuration } = useEditorTimeframe(creativeModel as any);
    const forceUpdate = useForceUpdate();
    const data = useRef<{ elements: any[] }>({ elements: [] });
    const controlRef = useRef<any>(null);
    const containerRef = useRef<any>(null);
    const [left, setLeft] = useState(0);
    const [width, setWidth] = useState(100);
    const style = useMemo(() => ({ left: `${left}%`, width: `${width}%` }), [left, width]);
    const { elements } = data.current;

    const updateElement = useCallback(() => {
        if (!creativeModel) {
            return;
        }

        data.current.elements = creativeModel.getElements().slice();
        forceUpdate();
    }, [creativeModel, forceUpdate]);

    useEffect(() => {
        updateElement();

        creativeModel.on('elementsTreeUpdated', updateElement);
        return () => creativeModel.off('elementsTreeUpdated', updateElement);
    }, [creativeModel, updateElement]);

    useEffect(() => {
        updateElement();
    }, [duration, timelineDuration]);

    const resizeEnd = useCallback(
        (leftPercentage: number, widthPercentage: number) => {
            onResizeEnd(leftPercentage, widthPercentage);
            setLeft(leftPercentage);
            setWidth(widthPercentage);
        },
        [onResizeEnd],
    );

    const dragEnd = useCallback((leftPercentage: number) => {
        setLeft(leftPercentage);
    }, []);

    const [onResize, onDoubleClick] = useZoomHandleResize(
        elements,
        controlRef,
        elementContainerRef,
        shotsContainerRef,
        timestampsContainerRef,
        duration,
    );

    const [onHandleMouseDown] = useResizing(controlRef, containerRef, onResize, resizeEnd, null, {
        minLeft: 20,
        minRight: 20,
    });

    const onControlMouseDown = useZoomDragging(
        controlRef,
        containerRef,
        elementContainerRef,
        shotsContainerRef,
        timestampsContainerRef,
        dragEnd,
        duration,
    );

    return (
        <ZoomContainer ref={containerRef}>
            <ZoomControlBox
                style={style}
                ref={controlRef}
                onMouseDown={onControlMouseDown}
                onDoubleClick={onDoubleClick}
            >
                <ZoomHandle className="--scalable" onMouseDown={(evt: any) => onHandleMouseDown(evt, 'left')} />
                <ZoomHandle className="--scalable" onMouseDown={(evt: any) => onHandleMouseDown(evt, 'right')} />
            </ZoomControlBox>
        </ZoomContainer>
    );
};
